import { ReadUnavailable, retryAfterMs, usePublicRead } from './public-reads';

export type StoryScope = 'men' | 'women' | 'academy' | 'club';
export interface LivingStory {
  id: string; canonicalUrl: string; title: string; summary: string;
  scope: StoryScope; kind: 'official' | 'reported' | 'opinion' | 'podcast' | 'video';
  summaryKind?: 'attributed-extract' | 'source-bound-summary';
  sourceId: string; sourceLabel: string; publishedAt: string | null; discoveredAt: string; updatedAt: string; reusePolicy: string;
}
export interface LivingSource {
  id: string; label: string; status: string; stale: boolean | null; enabled: boolean;
  lastSuccessAt: string | null; lastCheckedAt: string | null; error?: string | null;
  pendingCount: number; errorCount: number; articleError: string | null;
  rightsStatus?: string; permissionReference?: string | null; reviewedAt?: string | null;
}
export interface LivingFeed {
  unavailable?: boolean;
  stories: LivingStory[];
  edition: { date: string; title: string; summary: string; storyIds: string[]; updatedAt?: string } | null;
  sources: LivingSource[];
  /** Latest actual upstream check attempt, never the HTTP response generation time. */
  checkedAt: string | null;
  generatedAt?: string | null;
  limits?: Record<string, unknown>;
}
const EMPTY: LivingFeed = { stories: [], edition: null, sources: [], checkedAt: null };
const object = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));
const str = (value: unknown) => typeof value === 'string' ? value : '';
const date = (value: unknown): string | null => typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null;
const field = (row: Record<string, unknown>, camel: string, snake: string) => row[camel] ?? row[snake];

/** Reject malformed or unsafe outbound sources, and retain original source dates. */
export function parseLivingFeed(value: unknown): LivingFeed {
  if (!object(value) || !Array.isArray(value.stories) || !Array.isArray(value.sources)) throw new Error('living-invalid');
  const seen = new Set<string>();
  const stories = value.stories.flatMap((row): LivingStory[] => {
    if (!object(row)) return [];
    const id = str(row.id), canonicalUrl = str(row.sourceUrl ?? field(row, 'canonicalUrl', 'canonical_url'));
    const publishedAt = date(field(row, 'publishedAt', 'published_at'));
    const discoveredAt = date(field(row, 'discoveredAt', 'discovered_at'));
    const updatedAt = date(field(row, 'updatedAt', 'updated_at')) ?? publishedAt ?? discoveredAt;
    if (!id || seen.has(id) || !str(row.title).trim() || !updatedAt ||
      !['men', 'women', 'academy', 'club'].includes(str(row.scope)) ||
      !['official', 'reported', 'report', 'opinion', 'podcast', 'video'].includes(str(row.kind))) return [];
    try { if (new URL(canonicalUrl).protocol !== 'https:') return []; } catch { return []; }
    seen.add(id);
    return [{ id, canonicalUrl, title: str(row.title), summary: str(row.summary), scope: row.scope as StoryScope,
      kind: (row.kind === 'report' ? 'reported' : row.kind) as LivingStory['kind'], sourceId: str(field(row, 'sourceId', 'source_id')),
      sourceLabel: str(row.source ?? field(row, 'sourceLabel', 'source_label')) || 'Named source', publishedAt, discoveredAt: discoveredAt ?? updatedAt, updatedAt,
      reusePolicy: str(field(row, 'reusePolicy', 'reuse_policy')),
      summaryKind: ['attributed-extract', 'source-bound-summary'].includes(str(field(row, 'summaryKind', 'summary_kind'))) ? field(row, 'summaryKind', 'summary_kind') as LivingStory['summaryKind'] : undefined }];
  }).sort((a, b) => Date.parse(b.publishedAt ?? b.discoveredAt) - Date.parse(a.publishedAt ?? a.discoveredAt));
  let edition: LivingFeed['edition'] = null;
  if (object(value.edition) && /^\d{4}-\d{2}-\d{2}$/.test(str(value.edition.date)) &&
    date(value.edition.date) && str(value.edition.title).trim() && Array.isArray(field(value.edition, 'storyIds', 'story_ids'))) {
    const storyIds = (field(value.edition, 'storyIds', 'story_ids') as unknown[]).filter((id): id is string => typeof id === 'string' && seen.has(id));
    if (storyIds.length) edition = { date: str(value.edition.date), title: str(value.edition.title), summary: str(value.edition.summary), storyIds,
      updatedAt: date(field(value.edition, 'updatedAt', 'updated_at')) ?? date(value.edition.createdAt) ?? undefined };
  }
  const sources = value.sources.flatMap((row): LivingFeed['sources'] => object(row) && str(row.id) && str(row.label)
    ? [{ id: str(row.id), label: str(row.label), status: str(row.status) || 'unknown', enabled: row.enabled !== false && row.status !== 'disabled',
      stale: typeof row.stale === 'boolean' ? row.stale : null,
      rightsStatus: str(row.rightsStatus) || undefined, permissionReference: str(row.permissionReference) || null, reviewedAt: str(row.reviewedAt) || null,
      lastSuccessAt: date(field(row, 'lastSuccessAt', 'last_success_at')), lastCheckedAt: date(row.checkedAt ?? field(row, 'lastCheckedAt', 'last_checked_at')),
      error: str(row.error) || null, articleError: str(field(row, 'articleError', 'article_error')) || null,
      pendingCount: Number.isSafeInteger(row.pendingCount) && Number(row.pendingCount) >= 0 ? Number(row.pendingCount) : 0,
      errorCount: Number.isSafeInteger(row.errorCount) && Number(row.errorCount) >= 0 ? Number(row.errorCount) : 0 }] : []);
  const sourceChecks = sources.map((source) => source.lastCheckedAt).filter((value): value is string => value !== null).sort((a, b) => Date.parse(b) - Date.parse(a));
  return { stories, edition, sources, checkedAt: sourceChecks[0] ?? date(field(value, 'checkedAt', 'checked_at')),
    generatedAt: date(field(value, 'generatedAt', 'generated_at')),
    limits: object(value.limits) ? value.limits : undefined };
}

/** A successful discovery check is not proof that every article could be read. */
export function livingSourceHealth(source: LivingSource): string {
  if (source.status === 'permission-review' || ['blocked','unreviewed'].includes(source.rightsStatus ?? '')) return 'Source-use review required';
  if (!source.enabled || ['disabled', 'paused', 'budget-paused', 'budget_exhausted'].includes(source.status)) return 'Paused';
  if (source.error || source.articleError || source.errorCount > 0 || ['degraded', 'error', 'blocked', 'partial'].includes(source.status)) return 'Updates delayed';
  if (source.status === 'checking') return 'Checking';
  if (source.pendingCount > 0 || source.status === 'pending') return 'Articles awaiting a check';
  if (!source.lastCheckedAt && !source.lastSuccessAt) return 'No source check yet';
  if (!source.lastSuccessAt) return 'No successful check yet';
  if (source.stale === true) return 'Updates overdue';
  if (source.stale === null) return 'Freshness unknown';
  if (source.status === 'waiting' || source.status === 'checking') return 'Waiting for the next check';
  return ['healthy', 'fresh', 'ok', 'success', 'ready', 'no-change'].includes(source.status) ? 'Available' : 'Status unknown';
}

export function useLivingFeed(): LivingFeed {
  return usePublicRead({ key: 'living', initial: () => EMPTY, interval: () => 5 * 60_000,
    onError: (feed) => ({ ...feed, unavailable: true }),
    load: async () => {
      const response = await fetch('/api/living', { cache: 'no-store', signal: AbortSignal.timeout(15_000) });
      if (!response.ok) throw new ReadUnavailable(response.status, retryAfterMs(response.headers.get('retry-after')));
      return parseLivingFeed(await response.json());
    },
  });
}
